import * as React from "react";
import {
  ConversationStreamController,
  StreamSnapshot,
  EMPTY_SNAPSHOT,
} from "../common/services/stream";

// ChatStreamProvider owns the live chat WebSockets, one per conversation, and is
// mounted ABOVE the router (see App.tsx). Because the sockets live here rather
// than inside the Chat component, an in-flight generation keeps running while
// the user navigates the chat in and out of the tree, and multiple
// conversations can generate concurrently in the background. Components read a
// conversation's live state with `useConversationStream(id)` and the set of
// currently-generating conversations with `useStreamingIds()`.

type StartArgs = {
  text: string;
  patientId?: string;
  runId?: string | null;
  model?: string | null;
  replyId?: string | null;
  onSummary?: ((summary: string) => void) | null;
};

interface ChatStreamContextValue {
  startStream: (conversationId: string, args: StartArgs) => void;
  stopStream: (conversationId: string) => void;
  setSummaryHandler: (
    conversationId: string,
    fn: ((summary: string) => void) | null,
  ) => void;
  subscribeConversation: (conversationId: string, cb: () => void) => () => void;
  getConversationSnapshot: (conversationId: string) => StreamSnapshot;
  subscribeStreamingIds: (cb: () => void) => () => void;
  getStreamingIds: () => Set<string>;
}

const ChatStreamContext = React.createContext<ChatStreamContextValue | null>(null);

// Keep an ended controller around briefly so a remount within this window can
// finalize the answer in place and a late `summary` can still update the title.
const RETIRE_GRACE_MS = 120000;

export function ChatStreamProvider({ children }: { children: React.ReactNode }) {
  const controllersRef = React.useRef<Map<string, ConversationStreamController>>(new Map());
  const unsubsRef = React.useRef<Map<string, () => void>>(new Map());
  const retireTimersRef = React.useRef<Map<string, number>>(new Map());
  const perConvListenersRef = React.useRef<Map<string, Set<() => void>>>(new Map());
  const idsListenersRef = React.useRef<Set<() => void>>(new Set());
  const streamingIdsRef = React.useRef<Set<string>>(new Set());

  const notifyConversation = React.useCallback((id: string) => {
    perConvListenersRef.current.get(id)?.forEach((l) => l());
  }, []);

  const recomputeStreamingIds = React.useCallback(() => {
    const next = new Set<string>();
    controllersRef.current.forEach((ctrl, id) => {
      if (ctrl.isActive) next.add(id);
    });
    const prev = streamingIdsRef.current;
    let changed = prev.size !== next.size;
    if (!changed) {
      changed = Array.from(next).some((id) => !prev.has(id));
    }
    if (changed) {
      streamingIdsRef.current = next;
      idsListenersRef.current.forEach((l) => l());
    }
  }, []);

  const cancelRetire = React.useCallback((id: string) => {
    const t = retireTimersRef.current.get(id);
    if (t !== undefined) {
      window.clearTimeout(t);
      retireTimersRef.current.delete(id);
    }
  }, []);

  const disposeController = React.useCallback(
    (id: string) => {
      const ctrl = controllersRef.current.get(id);
      if (ctrl) ctrl.dispose();
      controllersRef.current.delete(id);
      const unsub = unsubsRef.current.get(id);
      if (unsub) {
        unsub();
        unsubsRef.current.delete(id);
      }
      cancelRetire(id);
      recomputeStreamingIds();
      notifyConversation(id);
    },
    [cancelRetire, recomputeStreamingIds, notifyConversation],
  );

  const scheduleRetire = React.useCallback(
    (id: string) => {
      cancelRetire(id);
      const t = window.setTimeout(() => disposeController(id), RETIRE_GRACE_MS);
      retireTimersRef.current.set(id, t);
    },
    [cancelRetire, disposeController],
  );

  const ensureController = React.useCallback(
    (conversationId: string, patientId?: string) => {
      let ctrl = controllersRef.current.get(conversationId);
      if (!ctrl) {
        ctrl = new ConversationStreamController({ patientId });
        controllersRef.current.set(conversationId, ctrl);
        const unsub = ctrl.subscribe(() => {
          notifyConversation(conversationId);
          recomputeStreamingIds();
          if (controllersRef.current.get(conversationId)?.getSnapshot().hasEnded) {
            scheduleRetire(conversationId);
          }
        });
        unsubsRef.current.set(conversationId, unsub);
      }
      return ctrl;
    },
    [notifyConversation, recomputeStreamingIds, scheduleRetire],
  );

  const startStream = React.useCallback(
    (conversationId: string, args: StartArgs) => {
      cancelRetire(conversationId);
      // A brand-new turn on this conversation: retire any ended controller first
      // so the new turn starts from a clean snapshot.
      const existing = controllersRef.current.get(conversationId);
      if (existing && existing.getSnapshot().hasEnded) {
        disposeController(conversationId);
      }
      const ctrl = ensureController(conversationId, args.patientId);
      if (args.onSummary !== undefined) ctrl.onSummary = args.onSummary;
      ctrl.ask({
        text: args.text,
        conversationId,
        replyId: args.replyId,
        runId: args.runId,
        model: args.model,
      });
      recomputeStreamingIds();
      notifyConversation(conversationId);
    },
    [cancelRetire, disposeController, ensureController, recomputeStreamingIds, notifyConversation],
  );

  const stopStream = React.useCallback(
    (conversationId: string) => {
      controllersRef.current.get(conversationId)?.stop();
      recomputeStreamingIds();
    },
    [recomputeStreamingIds],
  );

  const setSummaryHandler = React.useCallback(
    (conversationId: string, fn: ((summary: string) => void) | null) => {
      const ctrl = controllersRef.current.get(conversationId);
      if (ctrl) ctrl.onSummary = fn;
    },
    [],
  );

  const subscribeConversation = React.useCallback(
    (conversationId: string, cb: () => void) => {
      if (!conversationId) return () => {};
      let set = perConvListenersRef.current.get(conversationId);
      if (!set) {
        set = new Set();
        perConvListenersRef.current.set(conversationId, set);
      }
      set.add(cb);
      return () => {
        set?.delete(cb);
      };
    },
    [],
  );

  const getConversationSnapshot = React.useCallback(
    (conversationId: string): StreamSnapshot =>
      controllersRef.current.get(conversationId)?.getSnapshot() ?? EMPTY_SNAPSHOT,
    [],
  );

  const subscribeStreamingIds = React.useCallback((cb: () => void) => {
    idsListenersRef.current.add(cb);
    return () => {
      idsListenersRef.current.delete(cb);
    };
  }, []);

  const getStreamingIds = React.useCallback(() => streamingIdsRef.current, []);

  React.useEffect(() => {
    const controllers = controllersRef.current;
    const retireTimers = retireTimersRef.current;
    return () => {
      controllers.forEach((ctrl) => ctrl.dispose());
      controllers.clear();
      retireTimers.forEach((t) => window.clearTimeout(t));
      retireTimers.clear();
    };
  }, []);

  const value = React.useMemo<ChatStreamContextValue>(
    () => ({
      startStream,
      stopStream,
      setSummaryHandler,
      subscribeConversation,
      getConversationSnapshot,
      subscribeStreamingIds,
      getStreamingIds,
    }),
    [
      startStream,
      stopStream,
      setSummaryHandler,
      subscribeConversation,
      getConversationSnapshot,
      subscribeStreamingIds,
      getStreamingIds,
    ],
  );

  return <ChatStreamContext.Provider value={value}>{children}</ChatStreamContext.Provider>;
}

export function useChatStreamProvider(): ChatStreamContextValue {
  const ctx = React.useContext(ChatStreamContext);
  if (!ctx) {
    throw new Error("useChatStreamProvider must be used within a ChatStreamProvider");
  }
  return ctx;
}

/** Live snapshot of a single conversation's stream (EMPTY when none active). */
export function useConversationStream(
  conversationId: string | null | undefined,
): StreamSnapshot {
  const provider = useChatStreamProvider();
  const id = conversationId ?? "";
  const subscribe = React.useCallback(
    (cb: () => void) => provider.subscribeConversation(id, cb),
    [provider, id],
  );
  const getSnapshot = React.useCallback(
    () => provider.getConversationSnapshot(id),
    [provider, id],
  );
  return React.useSyncExternalStore(subscribe, getSnapshot);
}

/** The set of conversation ids currently generating (for the sidebar). */
export function useStreamingIds(): Set<string> {
  const provider = useChatStreamProvider();
  return React.useSyncExternalStore(provider.subscribeStreamingIds, provider.getStreamingIds);
}
